import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getActiveBranchId, setActiveBranchId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BranchContext = createContext({
  branches: [],
  activeBranch: null,
  activeBranchId: "",
  switchBranch: () => {},
  refresh: () => {},
});

export const BranchProvider = ({ children }) => {
  const [branches, setBranches] = useState([]);
  const [activeBranchId, setBranchId] = useState(() => getActiveBranchId());
  const { user, loading } = useAuth();

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/branches");
      const branchList = r.data || [];
      setBranches(branchList);
      const storedId = getActiveBranchId();
      const hasStored = !!storedId && branchList.some((b) => b.branch_id === storedId);
      const def = branchList.find((b) => b.is_default) || branchList[0];
      const nextId = hasStored ? storedId : (def?.branch_id || "");
      if (nextId !== storedId) {
        setActiveBranchId(nextId);
        setBranchId(nextId);
      } else if (!storedId && nextId) {
        setActiveBranchId(nextId);
        setBranchId(nextId);
      }
    } catch (err) {
      // Not authenticated yet — silent
      setBranches([]);
    }
  }, []);

  // Refresh branches whenever auth state changes (login/logout)
  useEffect(() => {
    if (loading) return;
    if (user) refresh();
    else setBranches([]);
  }, [user, loading, refresh]);

  const switchBranch = useCallback((id) => {
    setActiveBranchId(id);
    setBranchId(id);
    // Notify other tabs (so an admin with two open tabs stays consistent)
    window.dispatchEvent(new CustomEvent("branch-changed", { detail: { id } }));
    // Reload to refresh all data scoped to the new branch
    setTimeout(() => window.location.reload(), 100);
  }, []);

  const activeBranch = branches.find((b) => b.branch_id === activeBranchId) || null;

  return (
    <BranchContext.Provider value={{ branches, activeBranch, activeBranchId, switchBranch, refresh }}>
      {children}
    </BranchContext.Provider>
  );
};

export const useBranch = () => useContext(BranchContext);
